import EmblaCarousel from 'embla-carousel';

(function (once, Drupal) {
  Drupal.behaviors.emblaBehavior = {
    attach: function (context, settings) {
      once('emblaBehavior', '.embla-hero', context).forEach(
        function (rootNode) {
          // Configurable auto-scroll interval (in milliseconds)
          const AUTO_SCROLL_INTERVAL = 10000;

          // Grab wrapper nodes
          const viewportNode = rootNode.querySelector('.embla__viewport');
          const slides = rootNode.querySelectorAll('.embla__slide');
          const options = {
            containScroll: 'trimSnaps',
            align: 'start',
            slidesToScroll: 1,
            loop: true,
          };

          // Only initialize Embla if there's more than one slide
          let emblaHero = null;
          if (slides.length > 1) {
            // Initialize the carousel
            emblaHero = EmblaCarousel(viewportNode, options);
          }

          // --- Carousel Navigation ---
          // Get all slide titles
          const slideTitleNodes = rootNode.querySelectorAll(
            '.embla-hero-slide-title'
          );
          if (!slideTitleNodes.length) return;

          // Create nav container
          let navContainer = rootNode.querySelector('.embla-hero__nav');
          if (!navContainer) {
            navContainer = document.createElement('div');
            navContainer.className = 'embla-hero__nav';
            rootNode
              .querySelector('.embla__controls')
              .appendChild(navContainer);
          }
          navContainer.innerHTML = '';

          // Create nav blocks
          slideTitleNodes.forEach((titleNode, idx) => {
            const navBlock = document.createElement('button');
            navBlock.className = 'embla-hero__nav-block';
            navBlock.type = 'button';

            // Create background bar
            const bgBar = document.createElement('div');
            bgBar.className = 'embla-hero__nav-progress-bg';

            // Create progress bar and label container
            const progressBar = document.createElement('div');
            progressBar.className = 'embla-hero__nav-progress';

            const labelContainer = document.createElement('div');
            labelContainer.className = 'embla-hero__nav-label';
            labelContainer.innerHTML = titleNode.innerHTML;

            navBlock.appendChild(bgBar);
            navBlock.appendChild(progressBar);
            navBlock.appendChild(labelContainer);

            navBlock.addEventListener('click', () => {
              if (emblaHero) {
                emblaHero.scrollTo(idx);
                resetAutoScroll(); // Reset timer on manual navigation
              }
            });
            navContainer.appendChild(navBlock);
          });

          // Auto-scroll functionality
          let autoScrollTimer = null;

          function resetProgressBars() {
            navContainer
              .querySelectorAll('.embla-hero__nav-progress')
              .forEach((bar) => {
                bar.style.width = '0';
              });
          }

          function startProgressBar(selectedIdx) {
            const selectedBar = navContainer
              .querySelectorAll('.embla-hero__nav-block')
              [selectedIdx].querySelector('.embla-hero__nav-progress');

            // Reset all progress bars
            resetProgressBars();

            // Force browser reflow to ensure animation works
            void selectedBar.offsetWidth;

            // Start the animation
            selectedBar.style.transition = `width ${AUTO_SCROLL_INTERVAL}ms linear`;
            selectedBar.style.width = '100%';
          }

          function autoScroll() {
            // Scroll to next slide (only if emblaHero exists)
            if (emblaHero) {
              emblaHero.scrollNext();
            }
          }

          function resetAutoScroll() {
            // Skip if no emblaHero (single slide)
            if (!emblaHero) return;

            // Clear any existing timer
            clearTimeout(autoScrollTimer);

            // Start progress bar animation
            startProgressBar(emblaHero.selectedScrollSnap());

            // Set timer for next slide
            autoScrollTimer = setTimeout(autoScroll, AUTO_SCROLL_INTERVAL);
          }

          // Highlight active nav block and handle progress bar
          function updateNav() {
            // Skip if no emblaHero (single slide)
            if (!emblaHero) return;

            const selectedIdx = emblaHero.selectedScrollSnap();

            // Update active class
            navContainer
              .querySelectorAll('.embla-hero__nav-block')
              .forEach((block, idx) => {
                block.classList.toggle('is-active', idx === selectedIdx);
              });

            // Reset auto-scroll timer and progress bar
            resetAutoScroll();
          }

          // --- Fade-up animation logic ---
          const slideContentNodes = rootNode.querySelectorAll(
            '.embla__slide .container'
          );
          slideContentNodes.forEach((node) => {
            node.classList.add('fade-up');
          });

          function animateSlideContent(selectedIdx) {
            slideContentNodes.forEach((node, idx) => {
              if (idx === selectedIdx) {
                node.classList.remove('no-anim');
                node.classList.add('in-view');
              } else {
                node.classList.remove('in-view');
              }
            });
          }

          // If there is only one slide, handle it directly without Embla
          if (slides.length === 1) {
            setTimeout(() => {
              slideContentNodes[0]?.classList.remove('no-anim');
              slideContentNodes[0]?.classList.add('in-view');

              // Make sure the navigation is set up for single slide
              const navBlock = navContainer?.querySelector(
                '.embla-hero__nav-block'
              );
              if (navBlock) {
                navBlock.classList.add('is-active');
                const progressBar = navBlock.querySelector(
                  '.embla-hero__nav-progress'
                );
                if (progressBar) {
                  progressBar.style.transition = 'none';
                  progressBar.style.width = '100%';
                }
              }
            }, 50);
          }
          // If there are multiple slides, set up Embla events and animation
          else if (emblaHero) {
            // On initial load, animate the first slide's content
            setTimeout(() => {
              animateSlideContent(emblaHero.selectedScrollSnap());
            }, 50);

            // On slide change, animate the new slide's content
            emblaHero.on('select', () => {
              animateSlideContent(emblaHero.selectedScrollSnap());
            });

            // Listen for slide changes
            emblaHero.on('select', updateNav);

            // Initialize navigation and auto-scroll
            updateNav();
          }

          // Ensure background videos autoplay, loop, are muted, and have no controls
          rootNode
            .querySelectorAll('.hero-background-media video')
            .forEach((video) => {
              video.autoplay = true;
              video.loop = true;
              video.muted = true;
              video.playsInline = true;
              video.controls = false;
              video.load();
              video.play().catch(() => {});
            });

          // Helper function to check if a slide has image or video background
          function hasMediaBackground(slide) {
            const bgMedia = slide.querySelector('.hero-background-media');
            return (
              bgMedia &&
              (bgMedia.querySelector('img') || bgMedia.querySelector('video'))
            );
          }

          // Detect orange backgrounds and apply dark text mode class
          function applyDarkTextForOrangeBackgrounds() {
            const slideElements = rootNode.querySelectorAll('.embla__slide');
            slideElements.forEach((slideElement) => {
              // If there's an image or video background, don't apply dark text mode
              if (hasMediaBackground(slideElement)) {
                slideElement.classList.remove('dark-text-mode');
                return;
              }

              // Check for orange background (via class or color)
              if (
                slideElement.classList.contains('background-color-orange') ||
                isOrangeColor(getComputedStyle(slideElement).backgroundColor)
              ) {
                slideElement.classList.add('dark-text-mode');
              } else {
                slideElement.classList.remove('dark-text-mode');
              }
            });
          }

          // Helper function to detect orange colors
          function isOrangeColor(color) {
            // Extract RGB components
            const rgbMatch = color.match(
              /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/i
            );
            if (rgbMatch) {
              const r = parseInt(rgbMatch[1], 10);
              const g = parseInt(rgbMatch[2], 10);
              const b = parseInt(rgbMatch[3], 10);

              // Simple orange detection (high red, medium green, low blue)
              return r > 200 && g > 100 && g < 200 && b < 100;
            }

            // Add hex color detection if needed
            return false;
          }

          // This function is no longer used - removed for clarity

          // Update dark text mode when slide changes
          function updateDarkTextMode() {
            const slides = rootNode.querySelectorAll('.embla__slide');
            let currentIndex = 0;

            // Get current slide index from Embla if available
            if (emblaHero) {
              currentIndex = emblaHero.selectedScrollSnap();
            }

            const currentSlide = slides[currentIndex];
            const controls = rootNode.querySelector('.embla__controls');

            if (!currentSlide || !controls) return;

            // First, remove all selected classes from slides
            slides.forEach((slide) => {
              slide.classList.remove('is-selected');
            });

            // Mark current slide as selected
            currentSlide.classList.add('is-selected');

            // Remove any previous dark-text-mode classes from controls
            controls.classList.remove('dark-text-mode-controls');

            // If there's an image or video, explicitly remove dark text mode
            if (hasMediaBackground(currentSlide)) {
              currentSlide.classList.remove('dark-text-mode');
              return;
            }

            // Check if current slide has dark text mode or orange background
            if (
              currentSlide.classList.contains('dark-text-mode') ||
              currentSlide.classList.contains('background-color-orange')
            ) {
              controls.classList.add('dark-text-mode-controls');
            }
          }

          // Initial setup for all slides
          applyDarkTextForOrangeBackgrounds();

          // Set up event handling and initial slide setup
          setTimeout(() => {
            updateDarkTextMode();

            // For multiple slides, set up event listeners
            if (emblaHero) {
              emblaHero.on('select', updateDarkTextMode);
            }
          }, 100);
        }
      );
    },
  };
})(once, Drupal);
